import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Modal, Select, message } from "antd";
import {
  closePlaceholderTab,
  fetchFaxLines,
  isPlausibleFaxNumber,
  openPdfBlob,
  openPlaceholderTab,
  previewFax,
  sendDocumentFax,
} from "../../service/fax";
import FaxMatterSelect from "../../pages/Fax/components/FaxMatterSelect";

/**
 * "Send fax" modal: fax number, the firm line to send from, and the
 * cover-page To / Subject (filling either adds a branded cover page
 * as page one). Client-side plausibility gate shared with the Fax
 * page (the backend re-validates and is the authority), then
 * POST /documents/<id>/fax/. The queue receipt is instant; delivery
 * outcome arrives later as a FAX_SENT / FAX_FAILED notification, so
 * the modal only promises "queued".
 */

const fieldLabelStyle = {
  margin: "12px 0 4px",
  fontSize: 12,
  fontWeight: 600,
  color: "#666",
  textTransform: "uppercase",
};

const SendFaxModal = ({
  open,
  onClose,
  documentId,
  documentName,
  matterId: initialMatterId = "",
  matterName: initialMatterName = "",
}) => {
  const [toNumber, setToNumber] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [matterId, setMatterId] = useState(initialMatterId);
  const [coverTo, setCoverTo] = useState("");
  const [coverSubject, setCoverSubject] = useState("");
  const [faxLines, setFaxLines] = useState([]);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Placeholder tab for an in-flight preview: kept in a ref so
  // cancel/reset can close it instead of orphaning a blank tab
  // (Bugbot #253 round 2).
  const previewTabRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchFaxLines()
      .then((response) => {
        if (!cancelled) setFaxLines(response?.data?.results || []);
      })
      .catch(() => {
        // Dropdown quietly offers only "Firm default line" — the send
        // itself works without an explicit line.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) setMatterId(initialMatterId);
  }, [open, initialMatterId]);

  const reset = useCallback(() => {
    setToNumber("");
    setFromNumber("");
    setMatterId(initialMatterId);
    setCoverTo("");
    setCoverSubject("");
    setSending(false);
    setPreviewing(false);
    closePlaceholderTab(previewTabRef.current);
    previewTabRef.current = null;
  }, [initialMatterId]);

  const handleCancel = useCallback(() => {
    if (sending) return;
    reset();
    onClose?.();
  }, [sending, reset, onClose]);

  const handlePreview = useCallback(async () => {
    if (previewing || !documentId) return;
    setPreviewing(true);
    // Synchronous open inside the click gesture — see Bugbot #253.
    const tab = openPlaceholderTab();
    previewTabRef.current = tab;
    try {
      const response = await previewFax({
        toNumber: toNumber.trim(),
        documentId,
        coverTo: coverTo.trim(),
        coverSubject: coverSubject.trim(),
        fromNumber,
      });
      openPdfBlob(response.data, tab);
    } catch (error) {
      closePlaceholderTab(tab);
      const status = error?.response?.status;
      message.error(
        `Preview failed${status ? ` (HTTP ${status})` : ""}. Please try again.`
      );
    } finally {
      if (previewTabRef.current === tab) previewTabRef.current = null;
      setPreviewing(false);
    }
  }, [previewing, documentId, toNumber, coverTo, coverSubject, fromNumber]);

  const handleSend = useCallback(async () => {
    if (sending) return;
    if (!documentId || !isPlausibleFaxNumber(toNumber)) {
      message.error(
        "Enter a valid fax number: 10 digits (US) or +country code format."
      );
      return;
    }
    if (!matterId) {
      message.error("Select the matter this fax belongs to.");
      return;
    }
    setSending(true);
    try {
      const response = await sendDocumentFax(documentId, toNumber.trim(), {
        coverTo: coverTo.trim(),
        coverSubject: coverSubject.trim(),
        fromNumber,
        matterId,
      });
      const queuedTo = response?.data?.to_number || toNumber.trim();
      message.success(
        `Fax queued to ${queuedTo}. You'll get a notification when it's delivered.`
      );
      reset();
      onClose?.();
    } catch (error) {
      const status = error?.response?.status;
      const summary =
        error?.response?.data?.error?.summary ||
        `Failed to queue the fax${status ? ` (HTTP ${status})` : ""}. Please try again.`;
      message.error(summary);
      setSending(false);
    }
  }, [
    sending,
    documentId,
    toNumber,
    coverTo,
    coverSubject,
    fromNumber,
    matterId,
    reset,
    onClose,
  ]);

  return (
    <Modal
      open={open}
      title="Send fax"
      okText="Send fax"
      okButtonProps={{
        loading: sending,
        disabled: !isPlausibleFaxNumber(toNumber) || !matterId,
      }}
      cancelButtonProps={{ disabled: sending }}
      onOk={handleSend}
      onCancel={handleCancel}
      destroyOnClose
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <Button
            data-testid="send-fax-preview-btn"
            loading={previewing}
            disabled={sending}
            onClick={handlePreview}
            style={{ float: "left" }}
          >
            Preview
          </Button>
          <CancelBtn />
          <OkBtn />
        </>
      )}
    >
      <p>
        Fax <strong>{documentName || "this document"}</strong> to:
      </p>
      <Input
        placeholder="+1 (504) 555-1234"
        value={toNumber}
        onChange={(e) => setToNumber(e.target.value)}
        onPressEnter={handleSend}
        disabled={sending}
        autoFocus
        data-testid="send-fax-number-input"
      />

      <div style={fieldLabelStyle}>Send from</div>
      <Select
        style={{ width: "100%" }}
        value={fromNumber}
        onChange={setFromNumber}
        disabled={sending}
        data-testid="send-fax-from-line"
        options={[
          { value: "", label: "Firm default line" },
          ...faxLines.map((line) => ({
            value: line.number,
            label: `${line.label} — ${line.number}`,
          })),
        ]}
      />

      <div style={fieldLabelStyle}>Matter (required)</div>
      <FaxMatterSelect
        value={matterId}
        onChange={setMatterId}
        disabled={sending}
        currentMatterId={initialMatterId}
        currentMatterName={initialMatterName}
        dataTestId="send-fax-matter-select"
      />

      <div style={fieldLabelStyle}>To (cover page)</div>
      <Input
        placeholder="Recipient name, e.g. Dr. Alice Chen"
        value={coverTo}
        onChange={(e) => setCoverTo(e.target.value)}
        maxLength={255}
        disabled={sending}
        data-testid="send-fax-cover-to"
      />

      <div style={fieldLabelStyle}>Subject (cover page)</div>
      <Input
        placeholder="e.g. Records request — Claim C-12345"
        value={coverSubject}
        onChange={(e) => setCoverSubject(e.target.value)}
        maxLength={500}
        disabled={sending}
        data-testid="send-fax-cover-subject"
      />

      <p style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
        Filling in To or Subject adds a firm cover page as page one.
        Only PDF documents can be faxed. Double-check the number — the
        document will be transmitted to whoever answers it.
      </p>
    </Modal>
  );
};

SendFaxModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  documentId: PropTypes.string,
  documentName: PropTypes.string,
  matterId: PropTypes.string,
  matterName: PropTypes.string,
};

export default SendFaxModal;
